import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import type { OutboxRecord, RosterCropConfig, RosterRateCard, TeamMember } from '@/offline'
import {
  deliveryReducer,
  type DeliveryAction,
  type DeliveryDraft,
  type DraftDispute,
} from '../../model/delivery'
import { saveDeliveryDraft } from '../../data/actions'
import type { GateRoster } from '../../data/hooks'
import type { GateSession } from '../../data/session'

export interface WizardCtx {
  d: DeliveryDraft
  dispatch: (a: DeliveryAction) => void
  session: GateSession
  roster: GateRoster
  outbox: readonly OutboxRecord[]
  used: { bound: Set<string>; voided: Set<string> }
  actor: TeamMember
  crop: RosterCropConfig | undefined
  /** Usable card per stream for this crop on the collection date, or why not. */
  cards: {
    MAIN: RosterRateCard | null
    CONV: RosterRateCard | null
    problem: { stream: 'MAIN' | 'CONV'; kind: 'NO_CARD' | 'HAS_DEDUCTIONS' } | null
  }
  /** IST business date of the delivery (paper: the slip's date). */
  dateKey: string
  /** Timestamp for things done now (paper back-entry: the time on the slip). */
  stamp: () => string
  requestSupervisor: (reason: ReactNode) => Promise<TeamMember | null>
  startDispute: (
    prefill: Partial<Pick<DraftDispute, 'about' | 'container_id' | 'bag_position'>>,
  ) => void
}

export const WizardContext = createContext<WizardCtx | null>(null)

export function useWizard(): WizardCtx {
  const ctx = useContext(WizardContext)
  if (!ctx) throw new Error('useWizard outside the delivery wizard')
  return ctx
}

/** Draft state that is saved to IndexedDB after every action, in order. */
export function usePersistentDraft(initial: DeliveryDraft, actorId: string) {
  const [draft, setDraft] = useState(initial)
  const ref = useRef(initial)
  const queue = useRef<Promise<void>>(Promise.resolve())
  const dispatch = useCallback(
    (a: DeliveryAction) => {
      const next = deliveryReducer(ref.current, a)
      if (next === ref.current) return
      ref.current = next
      setDraft(next)
      queue.current = queue.current
        .then(() => saveDeliveryDraft(next, actorId))
        .catch(() => undefined)
    },
    [actorId],
  )
  const flush = useCallback(() => queue.current, [])
  return { draft, dispatch, flush }
}
